import { useContext, useState } from "react";
import axiosInstance from "../../helpers/AxiosInstance";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  Paper,
  Checkbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { selectedContext } from "../../pages/ShipmentsReport";

const CustomLink = styled(Link)`
  color: var(--primary-color);
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const StyledTableContainer = styled(TableContainer)`
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  margin-top: 20px;
  overflow-x: auto !important;
`;

const StyledTableHead = styled(TableHead)`
  & .MuiTableCell-head {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
    color: #1e293b !important;
    font-weight: 700 !important;
    font-size: 13px !important;
    border-bottom: 2px solid #e2e8f0 !important;
    padding: 14px 10px !important;
    font-family: "Cairo", sans-serif !important;
  }
`;

const StyledTableRow = styled(TableRow)`
  transition: all 0.2s ease !important;

  &:hover {
    background-color: #f8fafc !important;
    box-shadow: inset 0 0 0 1px rgba(var(--primary-rgb), 0.05) !important;
  }

  & .MuiTableCell-body {
    font-size: 13px !important;
    color: #475569 !important;
    border-bottom: 1px solid #f1f5f9 !important;
    padding: 12px 10px !important;
    font-family: "Cairo", sans-serif !important;
  }
`;

const ActionButton = styled(Button)`
  padding: 6px 12px !important;
  font-size: 11px !important;
  background: linear-gradient(
    135deg,
    var(--primary-color) 0%,
    rgba(var(--primary-rgb), 0.8) 100%
  ) !important;
  width: 80px !important;

  border: none !important;
  border-radius: 6px !important;
  font-family: "Cairo", sans-serif !important;
  font-weight: 600 !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  white-space: nowrap !important;
  text-transform: none !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3) !important;
  }

  &:active {
    transform: translateY(0);
  }
`;

// using react context may become more performant than passing state to props
const ReportsTable = (props) => {
  const { setSelected, selected } = useContext(selectedContext);
  let { rows = [], isLoading = false } = props;

  const { t, i18n } = useTranslation("reports");
  const { language } = i18n;

  // Modal states
  const [issueModalOpen, setIssueModalOpen] = useState(false);
  const [callLogModalOpen, setCallLogModalOpen] = useState(false);
  const [speedUpModalOpen, setSpeedUpModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [issueMessage, setIssueMessage] = useState("");
  const [callLogMessage, setCallLogMessage] = useState("");
  const [speedUpMessage, setSpeedUpMessage] = useState("");
  const [speedUpLoadingAwb, setSpeedUpLoadingAwb] = useState(null);
  const [issueSending, setIssueSending] = useState(false);
  const [issueError, setIssueError] = useState("");
  const [issueSent, setIssueSent] = useState(false);
  const [callLogData, setCallLogData] = useState([]);
  const [callLogLoading, setCallLogLoading] = useState(false);

  const instance = axiosInstance();

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Row selection
  const handleSelectRow = (awb) => {
    if (selected.includes(awb)) {
      setSelected(selected.filter((item) => item !== awb));
    } else {
      setSelected([...selected, awb]);
    }
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allAwbs = rows.map((row) => row.AWB);
      setSelected([...new Set([...selected, ...allAwbs])]);
    } else {
      setSelected([]);
    }
  };

  // Modal handlers - Issue
  const handleIssueOpen = (row) => {
    setSelectedRow(row);
    setIssueMessage("");
    setIssueModalOpen(true);
  };

  const handleIssueClose = () => {
    setIssueModalOpen(false);
    setSelectedRow(null);
    setIssueMessage("");
    setIssueError("");
    setIssueSent(false);
  };

  const handleIssueSend = async () => {
    if (!issueMessage.trim()) {
      setIssueError(language === "ar" ? "يرجى كتابة رسالة أولاً" : "Please enter a message first");
      return;
    }
    setIssueSending(true);
    setIssueError("");
    try {
      await instance.post("/SaveTicket", {
        subject: `${selectedRow?.AWB}`,
        AWB: `${selectedRow?.AWB}`,
        details: issueMessage,
        typeID: 10,
        mobile: "",
        phone: "",
        email: "",
      });
      setIssueSent(true);
      setTimeout(() => handleIssueClose(), 1500);
    } catch (err) {
      setIssueError(language === "ar" ? "حدث خطأ، يرجى المحاولة مرة أخرى" : "An error occurred. Please try again.");
    } finally {
      setIssueSending(false);
    }
  };

  // Modal handlers - Call Log
  const handleCallLogOpen = async (row) => {
    setSelectedRow(row);
    setCallLogData([]);
    setCallLogModalOpen(true);
    setCallLogLoading(true);
    try {
      const { data } = await instance.get(`/GetShipmentDetailsEx/${row.AWB}`);
      const normalized = data?.data || data;
      const comms = normalized?.RunnerComms || normalized?.runnerComms || [];
      setCallLogData(comms);
    } catch (err) {
      setCallLogData([]);
    } finally {
      setCallLogLoading(false);
    }
  };

  const handleCallLogClose = () => {
    setCallLogModalOpen(false);
    setSelectedRow(null);
    setCallLogData([]);
  };

  // Modal handlers - Speed Up
  const handleSpeedUpClose = () => {
    setSpeedUpModalOpen(false);
    setSelectedRow(null);
  };

  const handleSpeedUpClick = async (row) => {
    setSelectedRow(row);
    setSpeedUpLoadingAwb(row.AWB);
    try {
      await instance.post("/SaveTicket", {
        subject: `${row.AWB}`,
        AWB: `${row.AWB}`,
        details: "استعجال",
        typeID: 11,
        mobile: "",
        phone: "",
        email: "",
      });
      setSpeedUpModalOpen(true);
    } catch (err) {
      console.error(err);
      alert(language === "ar" ? "حدث خطأ، يرجى المحاولة مرة أخرى" : "An error occurred. Please try again.");
    } finally {
      setSpeedUpLoadingAwb(null);
    }
  };

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box dir={language === "ar" ? "rtl" : "ltr"}>
      <StyledTableContainer component={Paper}>
        <Table>
          <StyledTableHead>
            <TableRow>
              <TableCell padding="checkbox" style={{ minWidth: 60 }}>
                <Checkbox
                  indeterminate={
                    selected.length > 0 && selected.length < rows.length
                  }
                  checked={rows.length > 0 && selected.length === rows.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell style={{ minWidth: 150 }}>{t("awb")}</TableCell>
              <TableCell style={{ minWidth: 200 }}>{t("consignee")}</TableCell>
              <TableCell style={{ minWidth: 250 }}>{t("address")}</TableCell>
              <TableCell style={{ minWidth: 150 }}>{t("phone")}</TableCell>
              <TableCell style={{ minWidth: 150 }}>{t("city")}</TableCell>
              <TableCell style={{ minWidth: 100 }}>{t("weight")}</TableCell>
              <TableCell style={{ minWidth: 100 }}>{t("pieces")}</TableCell>
              <TableCell style={{ minWidth: 200 }}>{t("contents")}</TableCell>
              <TableCell style={{ minWidth: 120 }}>{t("cod")}</TableCell>
              <TableCell style={{ minWidth: 120 }}>{t("fees")}</TableCell>
              <TableCell style={{ minWidth: 180 }}>{t("status")}</TableCell>
              <TableCell style={{ minWidth: 350 }}>{t("actions")}</TableCell>
            </TableRow>
          </StyledTableHead>
          <TableBody>
            {paginatedRows.map((row, index) => (
              <StyledTableRow key={index}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(row.AWB)}
                    onChange={() => handleSelectRow(row.AWB)}
                  />
                </TableCell>
                <TableCell>
                  <CustomLink to={`/shipment/${row.AWB}`}>{row.AWB}</CustomLink>
                </TableCell>
                <TableCell>{row.ToConsigneeName || "-"}</TableCell>
                <TableCell>{row.ToAddress || "-"}</TableCell>
                <TableCell>{row.ToPhone || row.ToMobile || "-"}</TableCell>
                <TableCell>{row.ToCityName || "-"}</TableCell>
                <TableCell>{row.Weight || "-"}</TableCell>
                <TableCell>{row.Pieces || "-"}</TableCell>
                <TableCell>{row.Contents || "-"}</TableCell>
                <TableCell>{row.COD || "-"}</TableCell>
                <TableCell>{row.TotalFees || "-"}</TableCell>
                <TableCell
                  sx={{
                    backgroundColor:
                      row.StatusNameA === "تم التوصيل" ||
                      row.StatusNameA === "delivered"
                        ? "#23da63"
                        : row.StatusNameA === "Returned To Shipper" ||
                            row.StatusNameA === "تم الرد للراسل"
                          ? "#e42f2f"
                          : "#c9aa24",
                    color: "#ffffff",

                    padding: "6px 12px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: 600,
                    display: "inline-block",
                  }}
                >
                  {row.StatusNameA || "-"}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <ActionButton
                      onClick={() => handleIssueOpen(row)}
                      size="small"
                    >
                      � Issue
                    </ActionButton>
                    <ActionButton
                      onClick={() => handleCallLogOpen(row)}
                      size="small"
                    >
                      📞 Call
                    </ActionButton>
                    <ActionButton
                      onClick={() => handleSpeedUpClick(row)}
                      size="small"
                      disabled={speedUpLoadingAwb === row.AWB || issueSending || callLogLoading}
                    >
                      {speedUpLoadingAwb === row.AWB ? (
                        <CircularProgress size={16} sx={{ color: "#fff" }} />
                      ) : (
                        "⚡ Speed"
                      )}
                    </ActionButton>
                  </Box>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ fontFamily: "'Cairo', sans-serif" }}
      />
      {/* Issue Modal */}
      {/* Issue Modal */}
      <Dialog
        open={issueModalOpen}
        onClose={handleIssueClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, var(--primary-color) 0%, rgba(var(--primary-rgb), 0.8) 100%)`,
            color: "white",
            fontWeight: 700,
            fontSize: "18px",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          🔴 {t("issue")} - {selectedRow?.AWB}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {issueSent ? (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Box sx={{ fontSize: "48px", mb: 1 }}>✅</Box>
              <Box sx={{ fontFamily: "'Cairo', sans-serif", fontWeight: 700, color: "#16a34a", fontSize: "16px" }}>
                {language === "ar" ? "تم إرسال البلاغ بنجاح" : "Issue submitted successfully!"}
              </Box>
            </Box>
          ) : (
            <>
              <TextField
                autoFocus
                margin="dense"
                label={t("message")}
                type="text"
                fullWidth
                multiline
                rows={4}
                variant="outlined"
                value={issueMessage}
                onChange={(e) => { setIssueMessage(e.target.value); setIssueError(""); }}
                placeholder={t("enterYourMessage")}
                disabled={issueSending}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontFamily: "'Cairo', sans-serif",
                  },
                }}
              />
              {issueError && (
                <Box sx={{ color: "#dc2626", fontSize: "13px", mt: 1, fontFamily: "'Cairo', sans-serif" }}>
                  {issueError}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={handleIssueClose}
            disabled={issueSending}
            sx={{ fontFamily: "'Cairo', sans-serif" }}
          >
            {t("cancel")}
          </Button>
          {!issueSent && (
            <Button
              onClick={handleIssueSend}
              variant="contained"
              disabled={issueSending}
              sx={{
                background:
                  "linear-gradient(135deg, var(--primary-color) 0%, rgba(var(--primary-rgb), 0.8) 100%)",
                fontFamily: "'Cairo', sans-serif",
                fontWeight: 600,
                minWidth: 90,
              }}
            >
              {issueSending ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : t("send")}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Call Log Modal */}
      <Dialog
        open={callLogModalOpen}
        onClose={handleCallLogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, var(--primary-color) 0%, rgba(var(--primary-rgb), 0.8) 100%)`,
            color: "white",
            fontWeight: 700,
            fontSize: "18px",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          📞 {t("callLog")} - {selectedRow?.AWB}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1, px: 2, minHeight: 200 }}>
          {callLogLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 5 }}>
              <CircularProgress />
            </Box>
          ) : callLogData.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 5, color: "#94a3b8", fontFamily: "'Cairo', sans-serif" }}>
              <Box sx={{ fontSize: 40, mb: 1 }}>📵</Box>
              <Box sx={{ fontWeight: 600, fontSize: 15 }}>
                {language === "ar" ? "لا توجد مكالمات" : "No communications found"}
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", mt: 1 }}>
              {callLogData.map((comm, idx) => {
                const typeName = language === "ar" ? comm.CommTypeNameA : comm.CommTypeNameE;
                const runnerName = language === "ar" ? comm.RunnerNameA : comm.RunnerNameE;
                const template = language === "ar" ? comm.SMSTemplateA : comm.SMSTemplateE;
                const isWa = (comm.CommTypeNameE || "").toLowerCase().includes("whatsapp");
                const isSms = (comm.CommTypeNameE || "").toLowerCase().includes("sms") || (comm.CommTypeNameE || "").toLowerCase().includes("message");
                const iconColor = isWa ? "#25d366" : isSms ? "#3b82f6" : "var(--primary-color)";
                const bgColor = isWa ? "rgba(37,211,102,0.06)" : isSms ? "rgba(59,130,246,0.06)" : "rgba(67,95,134,0.06)";
                const borderColor = isWa ? "rgba(37,211,102,0.2)" : isSms ? "rgba(59,130,246,0.2)" : "rgba(67,95,134,0.15)";
                const emoji = isWa ? "💬" : isSms ? "📩" : "📞";
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex", gap: "12px", alignItems: "flex-start",
                      background: bgColor, border: `1px solid ${borderColor}`,
                      borderRadius: "10px", padding: "12px 14px",
                      fontFamily: "'Cairo', sans-serif",
                      direction: language === "ar" ? "rtl" : "ltr",
                    }}
                  >
                    <Box sx={{ fontSize: 22, flexShrink: 0, mt: "2px" }}>{emoji}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "4px", mb: "4px" }}>
                        <Box sx={{ fontWeight: 700, fontSize: 13.5, color: iconColor }}>{typeName}</Box>
                        {comm.CallPeriod > 0 && (
                          <Box sx={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                            {comm.CallPeriod} {language === "ar" ? "ث" : "sec"}
                          </Box>
                        )}
                      </Box>
                      {runnerName && (
                        <Box sx={{ fontSize: 12.5, color: "#64748b", mb: "4px" }}>
                          👤 {runnerName}
                        </Box>
                      )}
                      {template && (
                        <Box sx={{ fontSize: 12, color: "#475569", fontStyle: "italic", mb: "4px" }}>
                          "{template}"
                        </Box>
                      )}
                      <Box sx={{ fontSize: 11.5, color: "#94a3b8", mt: "2px" }}>
                        {new Date(comm.CallTime).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { year: "numeric", month: "short", day: "numeric" })}
                        {" · "}
                        {new Date(comm.CallTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCallLogClose} sx={{ fontFamily: "'Cairo', sans-serif" }}>
            {language === "ar" ? "إغلاق" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Speed Up Success Modal */}
      <Dialog
        open={speedUpModalOpen}
        onClose={handleSpeedUpClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, var(--primary-color) 0%, rgba(var(--primary-rgb), 0.8) 100%)`,
            color: "white",
            fontWeight: 700,
            fontSize: "18px",
            fontFamily: "'Cairo', sans-serif",
          }}
        >
          ⚡ {t("speedUp") || "Speed Up"} - {selectedRow?.AWB}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: "center", py: 3 }}>
            <Box sx={{ fontSize: "48px", mb: 1 }}>✅</Box>
            <Box sx={{ fontFamily: "'Cairo', sans-serif", fontWeight: 700, color: "#16a34a", fontSize: "16px" }}>
              {language === "ar" ? "تم استعجال شحنة بنجاح" : "Shipment speed up request submitted successfully!"}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleSpeedUpClose}
            sx={{ fontFamily: "'Cairo', sans-serif" }}
          >
            {language === "ar" ? "إغلاق" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportsTable;
